import React, { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getDesktopNotificationHost } from "./desktopNotificationHost";
import "./CustomAlert.css";

const ICONS = {
  info: "ℹ︎",
  success: "✓",
  warning: "⚠︎",
  error: "⨯",
  auth: "🔒",
};

const CustomAlert = ({
  message,
  type = "info", // info | success | warning | error | auth
  onClose,
  position,
  autoCloseMs = 2500,
  fadeOutMs = 300,
  actions = [],
}) => {
  const [isFading, setIsFading] = useState(false);
  const desktopHost = window.desktopAPI ? getDesktopNotificationHost() : null;
  useLayoutEffect(() => {
    if (desktopHost?.showPopover) {
      desktopHost.hidePopover?.();
      desktopHost.showPopover();
    }
  }, [desktopHost, message]);
  const ariaLive =
    type === "error" || type === "warning" || type === "auth"
      ? "assertive"
      : "polite";

  useEffect(() => {
    const fadeTimer = setTimeout(() => {
      setIsFading(true);
    }, Math.max(0, autoCloseMs - fadeOutMs));

    const closeTimer = setTimeout(() => {
      onClose?.();
    }, autoCloseMs);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(closeTimer);
    };
  }, [autoCloseMs, fadeOutMs, onClose]);

  const alert = (
    <div
      className={`custom-alert ${type} ${position === "top" ? "alert-top" : ""} ${isFading ? "fade-out" : ""}`}
      role="alert"
      aria-live={ariaLive}
      aria-atomic="true"
      onClick={desktopHost ? event => event.stopPropagation() : undefined}
    >
      <span className="icon" aria-hidden="true">
        {ICONS[type] || ICONS.info}
      </span>

      <span className="message">{message}</span>

      {actions?.length > 0 && (
        <div className="actions">
          {actions.map((action, index) => (
            <button
              key={index}
              className="action-btn"
              onClick={action.onClick}
              type="button"
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      <button
        className="close-btn app-cancel-control"
        onClick={onClose}
        aria-label="Close alert"
        type="button"
      >
        ✖
      </button>
    </div>
  );
  // A filtered/backdrop-filtered sidebar can become the containing block for
  // fixed descendants (notably in Firefox/Zen). Viewport banners belong outside
  // that narrow container; intentionally inline alerts remain in their caller.
  const viewportHost = desktopHost || (position === "top" ? document.body : null);
  return viewportHost ? createPortal(alert, viewportHost) : alert;
};

export default CustomAlert;
